import { useEffect, useMemo, useRef, useState } from "react";
import {
  ATTACHMENT_PREFIX,
  ITEM_PREFIX,
  TIME_ENTRY_PREFIX,
  type Attachment,
  type Group,
  type Item,
  type ItemPriority,
  type ItemRecurrence,
  type ItemStatus,
  type TimeEntry,
} from "../replicache/keys";
import { useReplicache, useSubscribe } from "../replicache/context";
import { uuidv7 } from "../replicache/uuid";
import { useAuth } from "../auth/context";
import { createShare, shareErrorMessage } from "../shares/api";
import { fetchContacts, type Contact } from "../contacts/api";
import { SharedWithPanel } from "../shares/SharedWithPanel";
import { ActivityWatchToggle } from "../shares/ActivityWatchToggle";
import { stageBlob } from "../attachments/staging";
import { getProgress, subscribeProgress } from "../attachments/uploader";
import { formatDmy, formatDmyTime } from "../format/date";
import { useSelection } from "./selection";
import { useUndo } from "./undo";

// Spec §13.6 / §8.1 — Detail pane.  Slides in from the right edge whenever an
// item is selected.  Edits flow through Replicache via updateItem; text fields
// debounce so we don't fire a mutation on every keystroke.

const STATUS_LABELS: Record<ItemStatus, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  blocked: "Blocked",
  done: "Done",
  cancelled: "Cancelled",
};

const STATUS_OPTIONS: ItemStatus[] = [
  "not_started",
  "in_progress",
  "blocked",
  "done",
  "cancelled",
];

const PRIORITY_LABELS: Record<ItemPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};
const PRIORITY_OPTIONS: ItemPriority[] = ["low", "medium", "high"];

const TEXT_DEBOUNCE_MS = 400;

export function ItemDetail({ groups }: { groups: Group[] }) {
  const rep = useReplicache();
  const { openId, open } = useSelection();

  // Look the item up via Replicache so edits across views stay in sync.
  const item = useSubscribe<Item | null>(
    rep,
    async (tx) => {
      if (!openId) return null;
      const value = (await tx.get(`${ITEM_PREFIX}${openId}`)) as
        | Item
        | undefined;
      return value ?? null;
    },
    null,
    [openId],
  );

  // Esc closes.  Listen at window level so it works regardless of focus.
  useEffect(() => {
    if (!openId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Don't fight an inline edit on the tree row — only close if no field
        // is being typed into within the detail pane (panel-level focus).
        const tag = (e.target as HTMLElement | null)?.tagName ?? "";
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        open(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId, open]);

  // (Pinch-zoom is disabled site-wide via the viewport meta tag — no
  // per-pane override needed.)

  if (!openId) return null;
  if (!item) {
    // Selected but item not yet loaded (or was just deleted).
    return (
      <aside className="safe-inset fixed inset-y-0 right-0 z-30 flex w-full max-w-md flex-col border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl lg:static lg:z-0 lg:w-80 lg:max-w-none lg:shrink-0 lg:p-0 lg:shadow-none xl:w-96">
        <header className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-4 py-2">
          <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Detail
          </span>
          <button
            onClick={() => open(null)}
            className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
            aria-label="Close detail pane"
          >
            ✕
          </button>
        </header>
        <p className="p-4 text-sm italic text-slate-500 dark:text-slate-400">Item not found.</p>
      </aside>
    );
  }

  return (
    <DetailForm
      key={item.id /* reset internal state on item switch */}
      item={item}
      groups={groups}
      onClose={() => open(null)}
    />
  );
}

// ---------------------------------------------------------------------------
// Form — receives a stable item and manages local drafts for text fields so
// keystrokes are smooth.  Date / status / reporting-to write through
// immediately; title and notes debounce.
// ---------------------------------------------------------------------------

function DetailForm({
  item,
  groups,
  onClose,
}: {
  item: Item;
  groups: Group[];
  onClose: () => void;
}) {
  const rep = useReplicache();
  const undo = useUndo();

  // Push an undo for a single-field edit.  `prev` is whatever the server-
  // committed value was just before the new one was written; the inverse
  // is the same mutator with `prev` substituted in.  Each save = one entry.
  const pushFieldUndo = (
    field:
      | "title"
      | "notes"
      | "reportingTo"
      | "status"
      | "priority"
      | "startDate"
      | "dueDate",
    prev: string | null,
    label: string,
  ) => {
    undo.push({
      label,
      undo: async () => {
        if (!rep) return;
        const args: Record<string, string | null> & { id: string } = {
          id: item.id,
        };
        args[field] = prev;
        await rep.mutate.updateItem(args as never);
      },
    });
  };

  // Local drafts for text fields.  Initialised once per item id; sync back if
  // the upstream item value changes (e.g. another tab edited it) and the user
  // isn't actively typing into the field.
  const [title, setTitle] = useState(item.title);
  const [notes, setNotes] = useState(item.notes);
  const [reportingTo, setReportingTo] = useState(item.reportingTo ?? "");

  const titleFocused = useRef(false);
  const notesFocused = useRef(false);
  const reportingFocused = useRef(false);

  useEffect(() => {
    if (!titleFocused.current) setTitle(item.title);
  }, [item.title]);
  useEffect(() => {
    if (!notesFocused.current) setNotes(item.notes);
  }, [item.notes]);
  useEffect(() => {
    if (!reportingFocused.current) setReportingTo(item.reportingTo ?? "");
  }, [item.reportingTo]);

  // --- Debounced field writers ------------------------------------------
  // One undo entry per debounced save = one per typing burst.  Pressing
  // undo while the input is still focused will set the server value back
  // and the input's onBlur-effect will pick it up; if you immediately keep
  // typing, the next debounced save just replaces it again.
  useDebouncedSave(title, item.title, (v) => {
    if (!rep) return;
    const prev = item.title;
    void rep.mutate.updateItem({ id: item.id, title: v });
    pushFieldUndo("title", prev, "Edit title");
  });
  useDebouncedSave(notes, item.notes, (v) => {
    if (!rep) return;
    const prev = item.notes;
    void rep.mutate.updateItem({ id: item.id, notes: v });
    pushFieldUndo("notes", prev, "Edit notes");
  });
  useDebouncedSave(reportingTo, item.reportingTo ?? "", (v) => {
    if (!rep) return;
    const prev = item.reportingTo;
    void rep.mutate.updateItem({
      id: item.id,
      reportingTo: v === "" ? null : v,
    });
    pushFieldUndo("reportingTo", prev, "Edit reporting-to");
  });

  // --- Immediate writers (selects + dates) -------------------------------
  const setStatus = (status: ItemStatus) => {
    if (!rep) return;
    const prev = item.status;
    if (prev === status) return;
    void rep.mutate.updateItem({ id: item.id, status });
    pushFieldUndo("status", prev, `Change status`);
  };
  const setPriority = (priority: ItemPriority | null) => {
    if (!rep) return;
    const prev = item.priority;
    if (prev === priority) return;
    void rep.mutate.updateItem({ id: item.id, priority });
    pushFieldUndo("priority", prev, "Change priority");
  };
  const setRecurrence = (recurrence: ItemRecurrence | null) => {
    if (!rep) return;
    if ((item.recurrence ?? null) === recurrence) return;
    void rep.mutate.updateItem({ id: item.id, recurrence });
  };
  const setDate = (field: "startDate" | "dueDate", value: string) => {
    if (!rep) return;
    const prev = field === "startDate" ? item.startDate : item.dueDate;
    const next = value === "" ? null : value;
    if (prev === next) return;
    void rep.mutate.updateItem({ id: item.id, [field]: next });
    pushFieldUndo(
      field,
      prev,
      field === "startDate" ? "Change start date" : "Change due date",
    );
  };
  const onDelete = async () => {
    if (!rep) return;
    if (!confirm(`Delete "${item.title || "(untitled)"}" and any child items?`)) {
      return;
    }
    await rep.mutate.deleteItem({ id: item.id });
    onClose();
  };
  const onArchive = async () => {
    if (!rep) return;
    if (
      !confirm(
        `Archive "${item.title || "(untitled)"}" and any child items? You can browse and restore archived items from the Archive page.`,
      )
    ) {
      return;
    }
    await rep.mutate.archiveItem({ id: item.id });
    onClose();
  };

  const group = groups.find((g) => g.id === item.groupId);

  // Normalise to plain YYYY-MM-DD for the HTML date inputs and min/max
  // attributes — old server builds returned full ISO datetimes for DATE
  // columns and those values can linger in the local IDB cache until the
  // next pull replaces them.
  const startISO = item.startDate ? item.startDate.slice(0, 10) : "";
  const dueISO = item.dueDate ? item.dueDate.slice(0, 10) : "";
  const dueMin = startISO || undefined;
  const startMax = dueISO || undefined;

  return (
    <aside className="safe-inset fixed inset-y-0 right-0 z-30 flex w-full max-w-md flex-col overflow-hidden border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl lg:static lg:z-0 lg:w-80 lg:max-w-none lg:shrink-0 lg:p-0 lg:shadow-none xl:w-96">
      <header className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-4 py-2">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {item.type === "header" ? "Header" : "Task"}
          {group ? ` · ${group.name}` : ""}
        </span>
        <button
          onClick={onClose}
          className="rounded p-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100"
          aria-label="Close detail pane"
        >
          ✕
        </button>
      </header>

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
        {/* Title */}
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onFocus={() => (titleFocused.current = true)}
          onBlur={() => (titleFocused.current = false)}
          placeholder="Untitled"
          className="w-full rounded border border-transparent bg-transparent px-1 py-1 text-lg font-semibold tracking-tight focus:border-slate-300 dark:focus:border-slate-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none"
        />

        {/* Focus toggle (tasks only).  Boolean column; multiple items may
            be starred — Focus Mode in Gantt filters down to these. */}
        {item.type === "task" && (
          <button
            type="button"
            onClick={() => {
              if (!rep) return;
              void rep.mutate.updateItem({
                id: item.id,
                isFocused: !item.isFocused,
              });
            }}
            aria-pressed={item.isFocused}
            className={`flex items-center gap-2 self-start rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
              item.isFocused
                ? "border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900"
                : "border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
            title="Toggle 'currently working on' (shortcut: F)"
          >
            <span
              aria-hidden
              className={item.isFocused ? "text-amber-500 dark:text-amber-400" : "text-slate-400 dark:text-slate-500"}
            >
              ★
            </span>
            <span>
              {item.isFocused
                ? "Currently working on"
                : "Mark as currently working on"}
            </span>
          </button>
        )}

        {/* Status + priority (tasks only; headers don't have either) */}
        {item.type === "task" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <select
                value={item.status}
                onChange={(e) => setStatus(e.target.value as ItemStatus)}
                className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-sm focus:border-slate-500 dark:focus:border-slate-600 focus:outline-none"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Priority">
              <select
                value={item.priority ?? ""}
                onChange={(e) =>
                  setPriority(
                    e.target.value === ""
                      ? null
                      : (e.target.value as ItemPriority),
                  )
                }
                className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-sm focus:border-slate-500 dark:focus:border-slate-600 focus:outline-none"
              >
                <option value="">None</option>
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABELS[p]}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date">
            <input
              type="date"
              value={startISO}
              max={startMax}
              onChange={(e) => setDate("startDate", e.target.value)}
              className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-sm focus:border-slate-500 dark:focus:border-slate-600 focus:outline-none"
            />
          </Field>
          <Field label="Due date">
            <input
              type="date"
              value={dueISO}
              min={dueMin}
              onChange={(e) => setDate("dueDate", e.target.value)}
              className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-sm focus:border-slate-500 dark:focus:border-slate-600 focus:outline-none"
            />
          </Field>
        </div>
        {/* Quick picks cover the common cases without the native picker. */}
        <div className="-mt-1 flex flex-wrap items-center gap-1.5">
          {(
            [
              ["Today", 0],
              ["Tomorrow", 1],
              ["Next week", 7],
            ] as const
          ).map(([label, days]) => (
            <button
              key={label}
              type="button"
              onClick={() => setDate("dueDate", isoFromToday(days))}
              className={`rounded-full border px-2 py-0.5 text-[11px] ${
                dueISO === isoFromToday(days)
                  ? "border-slate-500 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                  : "border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
            >
              {label}
            </button>
          ))}
          {dueISO && (
            <span
              className={`pl-1 text-[11px] ${
                relativeDue(dueISO).overdue ? "text-red-600 dark:text-red-400" : "text-slate-400 dark:text-slate-500"
              }`}
            >
              {relativeDue(dueISO).text}
            </span>
          )}
        </div>
        <Field label="Repeat">
          <select
            value={item.recurrence ?? ""}
            onChange={(e) =>
              setRecurrence(
                e.target.value === ""
                  ? null
                  : (e.target.value as ItemRecurrence),
              )
            }
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-sm focus:border-slate-500 dark:focus:border-slate-600 focus:outline-none"
          >
            <option value="">Never</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          {item.recurrence && !item.dueDate && (
            <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
              Set a due date — repeat advances it on each completion.
            </p>
          )}
          {item.recurrence && item.dueDate && (
            <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
              Completing this task moves it to the next{" "}
              {item.recurrence === "daily"
                ? "day"
                : item.recurrence === "weekly"
                  ? "week"
                  : "month"}{" "}
              instead of finishing it.
            </p>
          )}
        </Field>
        {(item.startDate || item.dueDate) && (
          <button
            onClick={() => {
              setDate("startDate", "");
              setDate("dueDate", "");
            }}
            className="-mt-2 self-start text-xs text-slate-500 dark:text-slate-400 underline hover:text-slate-800 dark:hover:text-slate-100"
          >
            Clear dates
          </button>
        )}

        {/* Reporting to */}
        <Field label="Reporting to">
          <input
            value={reportingTo}
            onChange={(e) => setReportingTo(e.target.value)}
            onFocus={() => (reportingFocused.current = true)}
            onBlur={() => (reportingFocused.current = false)}
            placeholder="—"
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-sm focus:border-slate-500 dark:focus:border-slate-600 focus:outline-none"
          />
        </Field>

        {/* Notes */}
        <Field label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onFocus={() => (notesFocused.current = true)}
            onBlur={() => (notesFocused.current = false)}
            placeholder="Plain text or Markdown…"
            rows={8}
            className="w-full resize-y rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 font-mono text-xs leading-5 focus:border-slate-500 dark:focus:border-slate-600 focus:outline-none"
          />
        </Field>

        {/* Time entries */}
        <TimeEntriesSection itemId={item.id} />

        {/* Attachments */}
        <AttachmentsSection itemId={item.id} />

        {/* Share links */}
        <ShareSection scope="item" scopeId={item.id} />

        {/* Metadata footer */}
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
          <dt>Created</dt>
          <dd>{formatDmyTime(item.createdAt)}</dd>
          <dt>Updated</dt>
          <dd>{formatDmyTime(item.updatedAt)}</dd>
        </dl>
      </div>

      <footer className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 px-4 py-2">
        <div className="flex items-center gap-3">
          <button
            onClick={() => void onArchive()}
            className="text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
            title="Archive: hide from live sync but keep browsable on the Archive page"
          >
            Archive
          </button>
          <button
            onClick={() => void onDelete()}
            className="text-xs font-medium text-rose-600 dark:text-rose-400 hover:text-rose-800 dark:hover:text-rose-300"
          >
            Delete item
          </button>
        </div>
        <span className="text-[11px] text-slate-400 dark:text-slate-500">F to star · Esc to close</span>
      </footer>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Small reusable label-wrapper.
// ---------------------------------------------------------------------------

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {label}
      {children}
    </label>
  );
}

// Fire `save(value)` once the user has stopped editing for TEXT_DEBOUNCE_MS,
// but only if the value actually differs from `committed` (the last known
// server value).  Cancelling the timeout on unmount avoids stale writes when
// the user closes the pane mid-typing.
function useDebouncedSave<T>(
  value: T,
  committed: T,
  save: (v: T) => void,
) {
  useEffect(() => {
    if (value === committed) return;
    const id = window.setTimeout(() => save(value), TEXT_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, committed]);
}

// ---------------------------------------------------------------------------
// Time entries section (§5.4 / §13.7)
// ---------------------------------------------------------------------------

function TimeEntriesSection({ itemId }: { itemId: string }) {
  const rep = useReplicache();

  // All items and time entries — needed for the rolled-up total over this
  // item's descendants.  Subscribing here keeps the figure live while the
  // user is editing children in another tab/view.
  const items = useSubscribe<Item[]>(
    rep,
    async (tx) => {
      const rows: Item[] = [];
      for await (const value of tx.scan({ prefix: ITEM_PREFIX }).values()) {
        rows.push(value as Item);
      }
      return rows;
    },
    [],
  );
  const entries = useSubscribe<TimeEntry[]>(
    rep,
    async (tx) => {
      const rows: TimeEntry[] = [];
      for await (const value of tx.scan({ prefix: TIME_ENTRY_PREFIX }).values()) {
        rows.push(value as TimeEntry);
      }
      return rows;
    },
    [],
  );

  // Entries for THIS item, newest first.
  const ownEntries = useMemo(
    () =>
      entries
        .filter((e) => e.itemId === itemId)
        .sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1)),
    [entries, itemId],
  );
  const ownTotal = useMemo(
    () => ownEntries.reduce((sum, e) => sum + e.durationMinutes, 0),
    [ownEntries],
  );

  // Roll-up total: sum entries for this item + any descendant items.
  const rolledTotal = useMemo(() => {
    const childrenOf = new Map<string | null, string[]>();
    for (const it of items) {
      const arr = childrenOf.get(it.parentId);
      if (arr) arr.push(it.id);
      else childrenOf.set(it.parentId, [it.id]);
    }
    const ids = new Set<string>([itemId]);
    const queue = [itemId];
    while (queue.length) {
      const head = queue.shift()!;
      const kids = childrenOf.get(head);
      if (!kids) continue;
      for (const k of kids) {
        if (!ids.has(k)) {
          ids.add(k);
          queue.push(k);
        }
      }
    }
    let sum = 0;
    for (const e of entries) {
      if (ids.has(e.itemId)) sum += e.durationMinutes;
    }
    return sum;
  }, [items, entries, itemId]);

  const hasDescendantEntries = rolledTotal > ownTotal;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Time
        </span>
        <span className="text-xs text-slate-600 dark:text-slate-300">
          <strong className="font-semibold text-slate-900 dark:text-slate-100">
            {formatMinutes(ownTotal)}
          </strong>
          {hasDescendantEntries && (
            <span className="text-slate-400 dark:text-slate-500">
              {" "}· {formatMinutes(rolledTotal)} incl. children
            </span>
          )}
        </span>
      </div>

      <ul className="space-y-1">
        {ownEntries.map((entry) => (
          <TimeEntryRow key={entry.id} entry={entry} />
        ))}
        {ownEntries.length === 0 && (
          <li className="text-xs italic text-slate-400 dark:text-slate-500">
            No entries yet.
          </li>
        )}
      </ul>

      <AddTimeEntryForm itemId={itemId} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// One row — clickable to expand into an inline editor; delete button.
// ---------------------------------------------------------------------------

function TimeEntryRow({ entry }: { entry: TimeEntry }) {
  const rep = useReplicache();
  const [editing, setEditing] = useState(false);
  const [started, setStarted] = useState(isoToLocalInput(entry.startedAt));
  const [ended, setEnded] = useState(isoToLocalInput(entry.endedAt));
  const [note, setNote] = useState(entry.note);

  useEffect(() => {
    if (editing) return;
    setStarted(isoToLocalInput(entry.startedAt));
    setEnded(isoToLocalInput(entry.endedAt));
    setNote(entry.note);
  }, [entry.startedAt, entry.endedAt, entry.note, editing]);

  const save = () => {
    if (!rep) return;
    const startedISO = localInputToISO(started);
    const endedISO = localInputToISO(ended);
    if (!startedISO || !endedISO) return;
    if (new Date(startedISO) > new Date(endedISO)) return;
    void rep.mutate.updateTimeEntry({
      id: entry.id,
      startedAt: startedISO,
      endedAt: endedISO,
      note,
    });
    setEditing(false);
  };

  const remove = () => {
    if (!rep) return;
    if (!confirm("Delete this time entry?")) return;
    void rep.mutate.deleteTimeEntry({ id: entry.id });
  };

  if (!editing) {
    return (
      <li
        className="group flex items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs"
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium text-slate-900 dark:text-slate-100">
              {formatMinutes(entry.durationMinutes)}
            </span>
            <span className="text-slate-500 dark:text-slate-400">
              {formatRange(entry.startedAt, entry.endedAt)}
            </span>
          </div>
          {entry.note && (
            <p className="mt-0.5 truncate text-slate-600 dark:text-slate-300">{entry.note}</p>
          )}
        </div>
        <div className="flex shrink-0 gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
          <button
            onClick={() => setEditing(true)}
            className="rounded px-1.5 py-0.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100"
          >
            Edit
          </button>
          <button
            onClick={remove}
            className="rounded px-1.5 py-0.5 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950 hover:text-rose-700 dark:hover:text-rose-300"
          >
            Delete
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="rounded border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-2 text-xs">
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Start
          </span>
          <input
            type="datetime-local"
            value={started}
            onChange={(e) => setStarted(e.target.value)}
            className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1 py-0.5"
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            End
          </span>
          <input
            type="datetime-local"
            value={ended}
            min={started || undefined}
            onChange={(e) => setEnded(e.target.value)}
            className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1 py-0.5"
          />
        </label>
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (optional)"
        className="mt-1 w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1 py-0.5"
      />
      <div className="mt-1 flex gap-2">
        <button
          onClick={save}
          className="rounded bg-slate-900 dark:bg-slate-100 px-2 py-0.5 text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200"
        >
          Save
        </button>
        <button
          onClick={() => setEditing(false)}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Add form — collapsed by default; "+ Add entry" reveals the inputs.
// Defaults: start = now − 30min, end = now.
// ---------------------------------------------------------------------------

function AddTimeEntryForm({ itemId }: { itemId: string }) {
  const rep = useReplicache();
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState("");
  const [ended, setEnded] = useState("");
  const [note, setNote] = useState("");

  const openForm = () => {
    const now = new Date();
    const half = new Date(now.getTime() - 30 * 60_000);
    setStarted(isoToLocalInput(half.toISOString()));
    setEnded(isoToLocalInput(now.toISOString()));
    setNote("");
    setOpen(true);
  };

  const submit = () => {
    if (!rep) return;
    const startedISO = localInputToISO(started);
    const endedISO = localInputToISO(ended);
    if (!startedISO || !endedISO) return;
    if (new Date(startedISO) > new Date(endedISO)) return;
    void rep.mutate.addTimeEntry({
      id: uuidv7(),
      itemId,
      startedAt: startedISO,
      endedAt: endedISO,
      note,
    });
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        onClick={openForm}
        className="self-start text-xs font-medium text-slate-600 dark:text-slate-300 underline hover:text-slate-900 dark:hover:text-slate-100"
      >
        + Add entry
      </button>
    );
  }

  return (
    <div className="rounded border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-2 text-xs">
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Start
          </span>
          <input
            type="datetime-local"
            value={started}
            onChange={(e) => setStarted(e.target.value)}
            className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1 py-0.5"
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            End
          </span>
          <input
            type="datetime-local"
            value={ended}
            min={started || undefined}
            onChange={(e) => setEnded(e.target.value)}
            className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1 py-0.5"
          />
        </label>
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (optional)"
        className="mt-1 w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1 py-0.5"
      />
      <div className="mt-1 flex gap-2">
        <button
          onClick={submit}
          className="rounded bg-slate-900 dark:bg-slate-100 px-2 py-0.5 text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200"
        >
          Save
        </button>
        <button
          onClick={() => setOpen(false)}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatMinutes(min: number): string {
  if (min < 1) return "0m";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function formatRange(startedISO: string, endedISO: string): string {
  const s = new Date(startedISO);
  const e = new Date(endedISO);
  const sameDay =
    s.getFullYear() === e.getFullYear() &&
    s.getMonth() === e.getMonth() &&
    s.getDate() === e.getDate();
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
  };
  if (sameDay) {
    return `${formatDmy(s)} · ${s.toLocaleTimeString(
      undefined,
      timeOpts,
    )}–${e.toLocaleTimeString(undefined, timeOpts)}`;
  }
  return `${formatDmy(s)} ${s.toLocaleTimeString(
    undefined,
    timeOpts,
  )} → ${formatDmy(e)} ${e.toLocaleTimeString(undefined, timeOpts)}`;
}

// datetime-local inputs use "YYYY-MM-DDTHH:mm" with NO timezone.  Convert
// from/to ISO at the boundary so the canonical store value carries a TZ.
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function localInputToISO(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Attachments section (§5.6 / §13.8)
// ---------------------------------------------------------------------------

const MAX_FILE_BYTES = 100 * 1024 * 1024; // 100 MB; matches server-side CHECK

function AttachmentsSection({ itemId }: { itemId: string }) {
  const rep = useReplicache();
  const { state } = useAuth();

  const userId =
    (state.status === "authenticated" && state.user.id) ||
    (state.status === "offline" && state.user?.id) ||
    null;

  const attachments = useSubscribe<Attachment[]>(
    rep,
    async (tx) => {
      const rows: Attachment[] = [];
      for await (const value of tx.scan({ prefix: ATTACHMENT_PREFIX }).values()) {
        const a = value as Attachment;
        if (a.itemId === itemId) rows.push(a);
      }
      rows.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
      return rows;
    },
    [],
    [itemId],
  );

  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | File[]) => {
    if (!rep || !userId) return;
    setUploadError(null);
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_BYTES) {
        setUploadError(
          `"${file.name}" is ${formatBytes(file.size)} — max ${formatBytes(MAX_FILE_BYTES)}.`,
        );
        continue;
      }
      const id = uuidv7();
      // Storage key is deterministic so server + client agree without an
      // extra round trip.  Includes user id for future bucket-policy scoping.
      const storageKey = `users/${userId}/att/${id}`;
      await stageBlob(id, file, file.name, file.type);
      await rep.mutate.createAttachment({
        id,
        itemId,
        filename: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        storageKey,
      });
      // The background uploader (started in ReplicacheProvider) sees the
      // new pending attachment, fetches a presigned URL, PUTs the blob, and
      // fires markAttachmentUploaded.
    }
  };

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Attachments
        </span>
        {attachments.length > 0 && (
          <span className="text-xs text-slate-500 dark:text-slate-400">{attachments.length}</span>
        )}
      </div>

      <ul className="space-y-1">
        {attachments.map((att) => (
          <AttachmentRow key={att.id} attachment={att} />
        ))}
      </ul>

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer?.files?.length) {
            void handleFiles(e.dataTransfer.files);
          }
        }}
        className={`flex cursor-pointer flex-col items-center justify-center rounded border-2 border-dashed px-3 py-4 text-center text-xs ${
          dragOver
            ? "border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300"
            : "border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
        }`}
      >
        <span>
          Drop a file here, or <span className="underline">click to choose</span>
        </span>
        <span className="mt-1 text-[10px] text-slate-400 dark:text-slate-500">
          Up to {formatBytes(MAX_FILE_BYTES)} per file.
        </span>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) void handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {uploadError && (
        <p className="text-xs text-rose-600 dark:text-rose-400">{uploadError}</p>
      )}
    </section>
  );
}

function AttachmentRow({ attachment }: { attachment: Attachment }) {
  const rep = useReplicache();
  const { state } = useAuth();
  const accessToken =
    (state.status === "authenticated" && state.session.access_token) ||
    (state.status === "offline" && state.session.access_token) ||
    null;

  const [downloading, setDownloading] = useState(false);

  // Subscribe to the module-level upload progress so we can render % while
  // the blob is in flight without going through Replicache.
  const [progress, setProgress] = useState<number | null>(() =>
    getProgress(attachment.id),
  );
  useEffect(() => {
    const sync = () => setProgress(getProgress(attachment.id));
    sync();
    return subscribeProgress(sync);
  }, [attachment.id]);

  const remove = async () => {
    if (!rep) return;
    if (!confirm(`Delete "${attachment.filename}"?`)) return;
    await rep.mutate.deleteAttachment({ id: attachment.id });
  };

  const download = async () => {
    if (!accessToken || downloading) return;
    if (attachment.uploadStatus !== "uploaded") return;
    setDownloading(true);
    try {
      const res = await fetch(
        `/api/attachments/${attachment.id}/download-url`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!res.ok) throw new Error(`download-url ${res.status}`);
      const { url } = (await res.json()) as { url: string };
      // Open in a new tab so the existing app state isn't disturbed by
      // any download/inline-view navigation.
      window.open(url, "_blank", "noopener");
    } catch (err) {
      alert(
        `Couldn't fetch download URL: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setDownloading(false);
    }
  };

  const statusBadge = () => {
    if (attachment.uploadStatus === "pending") {
      const pct = progress !== null ? Math.round(progress * 100) : null;
      return (
        <span className="rounded bg-amber-100 dark:bg-amber-900 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:text-amber-200">
          {pct !== null ? `Uploading ${pct}%` : "Uploading…"}
        </span>
      );
    }
    if (attachment.uploadStatus === "failed") {
      return (
        <span className="rounded bg-rose-100 dark:bg-rose-900 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 dark:text-rose-300">
          Failed
        </span>
      );
    }
    return null;
  };

  const showBar =
    attachment.uploadStatus === "pending" && progress !== null && progress < 1;

  return (
    <li className="group relative flex items-center gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {attachment.uploadStatus === "uploaded" ? (
            <button
              onClick={() => void download()}
              disabled={downloading}
              className="truncate text-left font-medium text-slate-900 dark:text-slate-100 underline-offset-2 hover:underline disabled:opacity-50"
              title={attachment.filename}
            >
              {attachment.filename}
            </button>
          ) : (
            <span
              className="truncate font-medium text-slate-700 dark:text-slate-200"
              title={attachment.filename}
            >
              {attachment.filename}
            </span>
          )}
          {statusBadge()}
        </div>
        <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
          {formatBytes(attachment.sizeBytes)}
        </div>
      </div>
      <button
        onClick={() => void remove()}
        className="shrink-0 rounded px-1.5 py-0.5 text-rose-600 dark:text-rose-400 opacity-0 hover:bg-rose-50 dark:hover:bg-rose-950 hover:text-rose-700 dark:hover:text-rose-300 group-hover:opacity-100"
        aria-label="Delete attachment"
      >
        Delete
      </button>
      {showBar && (
        <div
          aria-hidden
          className="absolute bottom-0 left-0 h-0.5 bg-sky-500 transition-[width] duration-150"
          style={{ width: `${(progress ?? 0) * 100}%` }}
        />
      )}
    </li>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// ---------------------------------------------------------------------------
// Share section (§5.7 / §13.9)
// ---------------------------------------------------------------------------

// "edit" still exists in the server schema (the JWT can be signed for any
// of the four levels) but it's hidden from the UI here until the
// interactive public viewer is wired up.  When that lands, add "edit"
// back to PERM_OPTIONS below.
type SharePermission = "read" | "read_tick" | "read_comment" | "edit";
type ShareScope = "group" | "item";

type ShareLink = {
  id: string;
  scope: ShareScope;
  scopeId: string;
  includeSubtree: boolean;
  permissions: SharePermission;
  expiresAt: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  useCount: number;
  accessToken: string;
  notifyOnView: boolean;
};

const PERM_LABELS: Record<SharePermission, string> = {
  read: "View only",
  read_tick: "View + tick tasks",
  read_comment: "View + add notes",
  edit: "Full edit",
};
const PERM_OPTIONS: SharePermission[] = ["read", "read_tick", "read_comment"];

const EXPIRY_OPTIONS: Array<{ label: string; days: number | null }> = [
  { label: "Never", days: null },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

export function ShareSection({
  scope,
  scopeId,
}: {
  scope: ShareScope;
  scopeId: string;
}) {
  const { state } = useAuth();
  const accessToken =
    (state.status === "authenticated" && state.session.access_token) ||
    (state.status === "offline" && state.session.access_token) ||
    null;

  const [links, setLinks] = useState<ShareLink[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  // Bumped whenever a user-targeted share is created / revoked /
  // permission-changed so the SharedWithPanel re-fetches.
  const [sharesRev, setSharesRev] = useState(0);
  const bumpShares = () => setSharesRev((n) => n + 1);

  const refresh = async () => {
    if (!accessToken) return;
    try {
      const res = await fetch(
        `/api/share-links?scopeId=${encodeURIComponent(scopeId)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = (await res.json()) as ShareLink[];
      setLinks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, accessToken]);

  const create = async (
    permissions: SharePermission,
    days: number | null,
    includeSubtree: boolean,
    notifyOnView: boolean,
  ) => {
    if (!accessToken) return;
    setCreating(true);
    setError(null);
    try {
      const expiresAt =
        days === null
          ? null
          : new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
      const res = await fetch("/api/share-links", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          scope,
          scopeId,
          // Group scope always includes the whole tree of items in the group;
          // includeSubtree only varies for item scope.
          includeSubtree: scope === "group" ? true : includeSubtree,
          permissions,
          expiresAt,
          notifyOnView,
        }),
      });
      if (!res.ok) throw new Error(`create ${res.status}`);
      await refresh();
      setShowCreate(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: string) => {
    if (!accessToken) return;
    if (!confirm("Revoke this share link?")) return;
    try {
      const res = await fetch(`/api/share-links/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error(`revoke ${res.status}`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <section className="flex flex-col gap-4">
      {accessToken && (
        <ActivityWatchToggle
          scope={scope}
          scopeId={scopeId}
          accessToken={accessToken}
        />
      )}
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Share with a person
          </span>
        </div>
        {accessToken && (
          <ShareWithPersonForm
            scope={scope}
            scopeId={scopeId}
            accessToken={accessToken}
            onCreated={bumpShares}
          />
        )}
        {accessToken && (
          <SharedWithPanel
            scope={scope}
            scopeId={scopeId}
            accessToken={accessToken}
            rev={sharesRev}
            onChanged={bumpShares}
          />
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Share links
          </span>
          {links !== null && links.length > 0 && (
            <span className="text-xs text-slate-500 dark:text-slate-400">{links.length}</span>
          )}
        </div>

        <ul className="space-y-1">
          {(links ?? []).map((link) => (
            <ShareLinkRow key={link.id} link={link} onRevoke={() => revoke(link.id)} />
          ))}
          {links !== null && links.length === 0 && !showCreate && (
            <li className="text-xs italic text-slate-400 dark:text-slate-500">No links yet.</li>
          )}
        </ul>

        {showCreate ? (
          <CreateShareForm
            scope={scope}
            creating={creating}
            onCancel={() => setShowCreate(false)}
            onCreate={create}
          />
        ) : (
          <button
            onClick={() => setShowCreate(true)}
            className="self-start text-xs font-medium text-slate-600 dark:text-slate-300 underline hover:text-slate-900 dark:hover:text-slate-100"
          >
            + New share link
          </button>
        )}

        {error && <p className="text-xs text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </section>
  );
}

function ShareWithPersonForm({
  scope,
  scopeId,
  accessToken,
  onCreated,
}: {
  scope: ShareScope;
  scopeId: string;
  accessToken: string;
  onCreated?: () => void;
}) {
  const [recipient, setRecipient] = useState("");
  const [includeSubtree, setIncludeSubtree] = useState(true);
  const [permissions, setPermissions] = useState<"read" | "edit">("read");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Load contacts once for the suggestion dropdown.  Cheap (single
  // request, small payload) and lets the picker feel instant.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const all = await fetchContacts(accessToken);
        if (alive) setContacts(all);
      } catch {
        /* dropdown stays empty; typed input still works */
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken]);

  // Filter contacts by what's been typed.  Handle leading @, otherwise
  // case-insensitive substring across handle / display name / email.
  const suggestions = (() => {
    const q = recipient.trim().toLowerCase().replace(/^@/, "");
    if (!q) return contacts.slice(0, 8);
    return contacts
      .filter((c) => {
        const handle = c.handle?.toLowerCase() ?? "";
        const name = (c.display_name ?? "").toLowerCase();
        const email = c.email.toLowerCase();
        return (
          handle.includes(q) || name.includes(q) || email.includes(q)
        );
      })
      .slice(0, 8);
  })();

  const submit = async () => {
    const target = recipient.trim();
    if (!target) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const res = await createShare(accessToken, {
        recipient: target,
        scope,
        scopeId,
        includeSubtree: scope === "group" ? true : includeSubtree,
        permissions,
      });
      if (res.status === "invited") {
        setDone(
          res.delivery.sent
            ? `Invite emailed to ${target}.`
            : `Invite created (email not sent: ${res.delivery.reason ?? "unknown"}).`,
        );
      } else {
        setDone("Share sent — they'll see it in their inbox.");
      }
      setRecipient("");
      onCreated?.();
      setTimeout(() => setDone(null), 4000);
    } catch (err) {
      setError(
        shareErrorMessage(err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setBusy(false);
    }
  };

  const pickContact = (c: Contact) => {
    setRecipient(c.handle ? `@${c.handle}` : c.email);
    setShowSuggestions(false);
    setHighlightedIndex(0);
  };

  return (
    <div className="rounded border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-2 text-xs">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            value={recipient}
            onChange={(e) => {
              setRecipient(e.target.value);
              setShowSuggestions(true);
              setHighlightedIndex(0);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => {
              // Defer so onClick on a suggestion fires before we hide it.
              setTimeout(() => setShowSuggestions(false), 120);
            }}
            placeholder="@handle or email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5"
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" && suggestions.length > 0) {
                e.preventDefault();
                setShowSuggestions(true);
                setHighlightedIndex((i) =>
                  Math.min(i + 1, suggestions.length - 1),
                );
              } else if (e.key === "ArrowUp" && suggestions.length > 0) {
                e.preventDefault();
                setShowSuggestions(true);
                setHighlightedIndex((i) => Math.max(i - 1, 0));
              } else if (e.key === "Enter") {
                if (showSuggestions && suggestions[highlightedIndex]) {
                  e.preventDefault();
                  pickContact(suggestions[highlightedIndex]);
                } else {
                  void submit();
                }
              } else if (e.key === "Escape") {
                setShowSuggestions(false);
              }
            }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul className="absolute left-0 right-0 top-full z-10 mt-0.5 max-h-56 overflow-auto rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-md">
              {suggestions.map((c, i) => {
                const handle = c.handle ? `@${c.handle}` : null;
                const name = c.display_name?.trim() || null;
                const isHighlighted = i === highlightedIndex;
                return (
                  <li
                    key={c.userId}
                    // onMouseDown fires before onBlur, so the input
                    // doesn't dismiss the dropdown before we get the
                    // click.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickContact(c);
                    }}
                    onMouseEnter={() => setHighlightedIndex(i)}
                    className={`flex cursor-pointer items-baseline gap-2 px-2 py-1 ${
                      isHighlighted ? "bg-slate-100 dark:bg-slate-800" : "hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                  >
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {handle ?? name ?? c.email.split("@")[0]}
                    </span>
                    <span className="truncate text-[10px] text-slate-500 dark:text-slate-400">
                      {c.email}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy || !recipient.trim()}
          className="rounded bg-slate-900 dark:bg-slate-100 px-2 py-0.5 font-medium text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-50"
        >
          {busy ? "Sending…" : "Send"}
        </button>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Permission
        </label>
        <select
          value={permissions}
          onChange={(e) =>
            setPermissions(e.target.value as "read" | "edit")
          }
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1 py-0.5"
          aria-label="Share permission"
        >
          <option value="read">View only</option>
          <option value="edit">Can edit</option>
        </select>
      </div>
      {scope === "item" && (
        <label className="mt-2 flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeSubtree}
            onChange={(e) => setIncludeSubtree(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-600"
          />
          <span className="text-slate-700 dark:text-slate-200">Include child items</span>
        </label>
      )}
      <p className="mt-2 text-[10px] italic text-slate-500 dark:text-slate-400">
        {permissions === "edit"
          ? "They'll be able to edit these items. Find the shared scope in their sidebar."
          : "They'll see a read-only snapshot."}{" "}
        Notified in-app, or by sign-in email if they don't have an account
        yet.
      </p>
      {error && <p className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">{error}</p>}
      {done && <p className="mt-1 text-[11px] text-emerald-700 dark:text-emerald-300">{done}</p>}
    </div>
  );
}

function CreateShareForm({
  scope,
  creating,
  onCancel,
  onCreate,
}: {
  scope: ShareScope;
  creating: boolean;
  onCancel: () => void;
  onCreate: (
    perm: SharePermission,
    days: number | null,
    includeSubtree: boolean,
    notifyOnView: boolean,
  ) => Promise<void>;
}) {
  const [perm, setPerm] = useState<SharePermission>("read");
  const [days, setDays] = useState<number | null>(7);
  const [includeSubtree, setIncludeSubtree] = useState(true);
  const [notifyOnView, setNotifyOnView] = useState(false);

  return (
    <div className="rounded border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-2 text-xs">
      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Permission
        </span>
        <select
          value={perm}
          onChange={(e) => setPerm(e.target.value as SharePermission)}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1 py-0.5"
        >
          {PERM_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {PERM_LABELS[p]}
            </option>
          ))}
        </select>
      </label>
      <label className="mt-2 flex flex-col gap-0.5">
        <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Expires
        </span>
        <select
          value={days === null ? "" : String(days)}
          onChange={(e) =>
            setDays(e.target.value === "" ? null : Number(e.target.value))
          }
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1 py-0.5"
        >
          {EXPIRY_OPTIONS.map((o) => (
            <option key={o.label} value={o.days ?? ""}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      {scope === "item" && (
        <label className="mt-2 flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeSubtree}
            onChange={(e) => setIncludeSubtree(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-600"
          />
          <span className="text-slate-700 dark:text-slate-200">Include child items</span>
        </label>
      )}
      <label className="mt-2 flex items-center gap-2">
        <input
          type="checkbox"
          checked={notifyOnView}
          onChange={(e) => setNotifyOnView(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-600"
        />
        <span className="text-slate-700 dark:text-slate-200">Notify me when viewed</span>
      </label>
      {perm !== "read" && (
        <p className="mt-2 text-[10px] italic text-amber-700 dark:text-amber-300">
          Interactive permissions (tick / comment / edit) are signed correctly
          but the public viewer is read-only in this build. They'll start
          working in the next phase.
        </p>
      )}
      <div className="mt-2 flex gap-2">
        <button
          onClick={() => void onCreate(perm, days, includeSubtree, notifyOnView)}
          disabled={creating}
          className="rounded bg-slate-900 dark:bg-slate-100 px-2 py-0.5 text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-50"
        >
          {creating ? "Creating…" : "Create"}
        </button>
        <button
          onClick={onCancel}
          disabled={creating}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ShareLinkRow({
  link,
  onRevoke,
}: {
  link: ShareLink;
  onRevoke: () => void;
}) {
  const [copied, setCopied] = useState<null | "link" | "embed">(null);

  const url = `${window.location.origin}/shared/${link.accessToken}`;
  // Same token, compact chrome-less view meant for pasting into an Excalidraw
  // board's web-embed box (see EmbedView / ExcalidrawView.renderEmbeddable).
  const embedUrl = `${window.location.origin}/embed/${link.accessToken}`;

  const copy = async (which: "link" | "embed") => {
    const value = which === "link" ? url : embedUrl;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Some browsers gate clipboard on user-activation events; fall back
      // to selection-friendly prompt.
      window.prompt("Copy this link:", value);
    }
  };

  const expired =
    link.expiresAt !== null && new Date(link.expiresAt).getTime() < Date.now();

  return (
    <li className="group flex items-center gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {PERM_LABELS[link.permissions]}
          </span>
          {link.notifyOnView && (
            <span
              title="You'll get a notification each time this link is viewed"
              className="text-slate-500 dark:text-slate-400"
              aria-label="Notifications on"
            >
              🔔
            </span>
          )}
          {expired && (
            <span className="rounded bg-rose-100 dark:bg-rose-900 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 dark:text-rose-300">
              Expired
            </span>
          )}
        </div>
        <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
          {link.expiresAt
            ? `Expires ${formatDmy(link.expiresAt)}`
            : "No expiry"}
          {link.useCount > 0 && ` · ${link.useCount} use${link.useCount === 1 ? "" : "s"}`}
        </div>
      </div>
      <button
        onClick={() => void copy("link")}
        className="shrink-0 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1.5 py-0.5 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        {copied === "link" ? "Copied!" : "Copy link"}
      </button>
      <button
        onClick={() => void copy("embed")}
        title="Copy a URL for embedding this list in an Excalidraw board's web-embed box"
        className="shrink-0 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1.5 py-0.5 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        {copied === "embed" ? "Copied!" : "Copy embed URL"}
      </button>
      <button
        onClick={onRevoke}
        className="shrink-0 rounded px-1.5 py-0.5 text-rose-600 dark:text-rose-400 opacity-0 hover:bg-rose-50 dark:hover:bg-rose-950 hover:text-rose-700 dark:hover:text-rose-300 group-hover:opacity-100"
      >
        Revoke
      </button>
    </li>
  );
}

// --- Date quick-pick helpers -------------------------------------------------

function isoFromToday(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function relativeDue(dueISO: string): { text: string; overdue: boolean } {
  const today = isoFromToday(0);
  const msPerDay = 24 * 60 * 60 * 1000;
  const diff = Math.round(
    (new Date(dueISO + "T00:00").getTime() -
      new Date(today + "T00:00").getTime()) /
      msPerDay,
  );
  if (diff === 0) return { text: "due today", overdue: false };
  if (diff === 1) return { text: "due tomorrow", overdue: false };
  if (diff > 1) return { text: `due in ${diff} days`, overdue: false };
  if (diff === -1) return { text: "overdue by 1 day", overdue: true };
  return { text: `overdue by ${-diff} days`, overdue: true };
}
